import type {Object3D} from 'three';
import type {SketchGroup} from '../../../../mesh/sketch/SketchGroup';
import type {CollectSource} from '../../../../mesh/sketch/ISketchObject';
import {isSketchCircleObject, isSketchEllipseObject, isSketchSplineObject} from '../../../../mesh/guards';
import {SketchClosureCandidateFilter} from '../../../../sketch/closure';
import type {
	SketchEntityReferenceClosedProfileFinalizeInput,
	SketchEntityReferenceClosedProfileFinalizeResult,
	SketchEntityReferenceClosedProfileFinalizePort,
	StandaloneContourOwner,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceClosedProfileFinalizer';

export type {
	SketchEntityReferenceClosedProfileFinalizeInput,
	SketchEntityReferenceClosedProfileFinalizeResult,
	SketchEntityReferenceClosedProfileFinalizePort,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceClosedProfileFinalizer';

/**
 * 转换实体引用闭合轮廓收口器。
 *
 * 说明：
	 * 1. 线、圆弧、部分椭圆、抛物线、样条等边链通过 `SketchGroup.tryFinalizeClosedSketchCollection` 收口；
 * 2. 圆、完整椭圆与闭合样条只在开关开启时同步自身轮廓；
 * 3. 上色草图轮廓开关只影响显示对象，不影响闭合 profile 的语义。
 */
export class SketchEntityReferenceClosedProfileFinalizer implements SketchEntityReferenceClosedProfileFinalizePort {
	/** 草图闭合候选过滤器。 */
	private readonly closureCandidateFilter = new SketchClosureCandidateFilter();

	/**
	 * 对本次转换实体引用创建出的对象执行闭合轮廓收口。
	 *
	 * @param input 收口输入。
	 * @returns 收口结果。
	 */
	public finalize(
		input: SketchEntityReferenceClosedProfileFinalizeInput,
	): SketchEntityReferenceClosedProfileFinalizeResult {
		const createdCollectionIds = new Set<string>();
		const syncedStandaloneContourOwnerIds = new Set<string>();
		const targetObjects = this.uniqueObjects(input.targetObjects);

		// 自闭合对象不参与集合收口。它们的 profile 语义来自自身几何，只需要按开关同步显示轮廓。
		targetObjects.forEach((targetObject) => {
			if (!this.isStandaloneContourOwner(targetObject)) {
				return;
			}
			if (!input.syncContour || !targetObject.canCreateContour()) {
				return;
			}
			targetObject.syncContour();
			syncedStandaloneContourOwnerIds.add(targetObject.persistentId);
		});

		// 多边闭合链统一交给 SketchGroup 的闭合入口，避免 Convert Entities 重复实现闭合拓扑。
		this.collectClosureSeeds(targetObjects).forEach((seedSource) => {
			const collection = input.targetSketch.tryFinalizeClosedSketchCollection(
				[seedSource],
				{ syncContour: input.syncContour },
			);
			if (collection) {
				createdCollectionIds.add(collection.persistentId);
			}
		});

		return {
			createdCollectionIds: [...createdCollectionIds],
			syncedStandaloneContourOwnerIds: [...syncedStandaloneContourOwnerIds],
		};
	}

	/**
	 * 对对象列表去重并保留原始顺序。
	 *
	 * @param objects 原始对象列表。
	 * @returns 去重后的对象列表。
	 */
	private uniqueObjects(objects: readonly Object3D[]): Object3D[] {
		const objectMap = new Map<string, Object3D>();
		objects.forEach((object) => {
			if (!objectMap.has(object.uuid)) {
				objectMap.set(object.uuid, object);
			}
		});
		return [...objectMap.values()];
	}

	/**
	 * 收集可用于闭合集合收口的种子边。
	 *
	 * @param objects 本次创建出的目标对象。
	 * @returns 可参与闭合收口的种子边。
	 */
	private collectClosureSeeds(objects: readonly Object3D[]): CollectSource[] {
		return objects.filter((object): object is CollectSource => (
			!this.isStandaloneContourOwner(object)
			&& this.closureCandidateFilter.isCollectSourceForSketchClosure(object)
			&& this.closureCandidateFilter.isCollectSourceAvailableForSketchClosure(object)
		));
	}

	/**
	 * 判断对象是否为可直接拥有自身轮廓的自闭合草图对象。
	 *
	 * @param object 待判断对象。
	 * @returns 可直接同步自身轮廓时返回 true。
	 */
	private isStandaloneContourOwner(object: Object3D): object is StandaloneContourOwner {
		return isSketchCircleObject(object)
			|| (isSketchEllipseObject(object) && !object.isPartialEllipse())
			// 与原始绘制一样，单条闭合样条自带轮廓，不参与至少两条边的闭合集合搜索。
			|| (isSketchSplineObject(object) && object.getCurveMetadata().closureKind === 'closed');
	}
}

